<template>
  <el-autocomplete
    v-model="displayValue"
    class="event-link-autocomplete"
    :fetch-suggestions="fetchSuggestions"
    :debounce="300"
    :trigger-on-focus="false"
    clearable
    :placeholder="placeholder"
    popper-class="event-link-autocomplete-popper"
    @input="handleInput"
    @select="handleSelect"
    @clear="handleClear"
  >
    <template slot-scope="{ item }">
      <div class="event-link-option">
        <div class="event-link-option-name">{{ item.name }}</div>
        <div class="event-link-option-meta">
          <span>{{ item._id }}</span>
          <span v-if="formatMeta(item)"> · {{ formatMeta(item) }}</span>
        </div>
      </div>
    </template>
  </el-autocomplete>
</template>

<script>
const OBJECT_ID_RE = /^[a-f\d]{24}$/i;

export default {
  name: 'EventLinkAutocomplete',
  props: {
    value: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: '可输入页面路径、HTTPS 链接、赛事 ID 或赛事关键词'
    }
  },
  data() {
    return {
      displayValue: this.value || '',
      selectedEventId: '',
      selectedEventLabel: '',
      requestId: 0,
      suggestionKeyword: '',
      suggestions: [],
      suggestionTotal: 0,
      suggestionPageNum: 1,
      suggestionPageSize: 10,
      suggestionLoading: false,
      suggestionLoadingMore: false,
      suggestionDone: null,
      suggestionScrollElement: null
    };
  },
  beforeDestroy() {
    this.detachSuggestionScroll();
  },
  watch: {
    value(nextValue) {
      if (nextValue === this.selectedEventId && this.selectedEventLabel) {
        this.displayValue = this.selectedEventLabel;
        return;
      }
      this.selectedEventId = '';
      this.selectedEventLabel = '';
      this.displayValue = nextValue || '';
    }
  },
  methods: {
    shouldSkipSuggest(value) {
      const text = String(value || '').trim();
      return !text ||
        text.startsWith('/pages') ||
        /^https:\/\//i.test(text) ||
        OBJECT_ID_RE.test(text);
    },
    normalizeKeyword(value) {
      return String(value || '').trim().replace(/[,，、]+/g, ' ');
    },
    fetchSuggestions(queryString, done) {
      const keyword = this.normalizeKeyword(queryString);
      this.suggestionDone = done;
      if (this.shouldSkipSuggest(keyword)) {
        this.detachSuggestionScroll();
        this.suggestionKeyword = '';
        this.suggestions = [];
        this.suggestionTotal = 0;
        done([]);
        return;
      }
      this.suggestionKeyword = keyword;
      this.suggestionPageNum = 1;
      this.suggestionTotal = 0;
      this.suggestions = [];
      this.loadSuggestions(true, done);
    },
    loadSuggestions(reset, done = this.suggestionDone) {
      if (!this.suggestionKeyword || !done || this.suggestionLoading || this.suggestionLoadingMore) return;
      if (!reset && this.suggestions.length >= this.suggestionTotal) return;
      const currentRequestId = ++this.requestId;
      const scrollTop = this.suggestionScrollElement ? this.suggestionScrollElement.scrollTop : 0;
      if (reset) {
        this.suggestionLoading = true;
      } else {
        this.suggestionLoadingMore = true;
      }
      this.$axios
        .get('/content-event/search_active', {
          params: {
            keyword: this.suggestionKeyword,
            pageNum: this.suggestionPageNum,
            pageSize: this.suggestionPageSize
          }
        })
        .then((res) => {
          if (currentRequestId !== this.requestId) return;
          const list = (res.data && res.data.res && res.data.res.list) || [];
          this.suggestionTotal = (res.data && res.data.res && res.data.res.total) || 0;
          const nextSuggestions = list.map(event => ({
            ...event,
            value: `${event.name}（${event._id}）`
          }));
          if (reset) {
            this.suggestions = nextSuggestions;
          } else {
            const existingIds = new Set(this.suggestions.map(event => String(event._id)));
            this.suggestions = this.suggestions.concat(
              nextSuggestions.filter(event => !existingIds.has(String(event._id)))
            );
          }
          done(this.suggestions);
          this.$nextTick(() => {
            this.attachSuggestionScroll();
            if (!reset && this.suggestionScrollElement) {
              this.suggestionScrollElement.scrollTop = scrollTop;
            }
          });
        })
        .catch(() => {
          if (currentRequestId === this.requestId) {
            if (reset) {
              done([]);
            }
          }
        })
        .finally(() => {
          if (currentRequestId === this.requestId) {
            this.suggestionLoading = false;
            this.suggestionLoadingMore = false;
          }
        });
    },
    attachSuggestionScroll() {
      this.detachSuggestionScroll();
      const scrollElement = document.querySelector(
        '.event-link-autocomplete-popper .el-scrollbar__wrap, .event-link-autocomplete-popper .el-autocomplete-suggestion__wrap'
      );
      if (!scrollElement) return;
      this.suggestionScrollElement = scrollElement;
      scrollElement.addEventListener('scroll', this.handleSuggestionScroll);
    },
    detachSuggestionScroll() {
      if (!this.suggestionScrollElement) return;
      this.suggestionScrollElement.removeEventListener('scroll', this.handleSuggestionScroll);
      this.suggestionScrollElement = null;
    },
    handleSuggestionScroll(event) {
      const element = event.target;
      const reachedBottom = element.scrollHeight - element.scrollTop - element.clientHeight < 24;
      if (!reachedBottom || this.suggestionLoading || this.suggestionLoadingMore) return;
      if (this.suggestions.length >= this.suggestionTotal) return;
      this.suggestionPageNum += 1;
      this.loadSuggestions(false);
    },
    handleInput(value) {
      this.selectedEventId = '';
      this.selectedEventLabel = '';
      this.$emit('input', value);
    },
    handleSelect(item) {
      if (item && item.isLoadingMore) return;
      this.selectedEventId = String(item._id || '');
      this.selectedEventLabel = item.value || this.selectedEventId;
      this.displayValue = this.selectedEventLabel;
      this.$emit('input', this.selectedEventId);
      this.$emit('select-event', item);
    },
    handleClear() {
      this.selectedEventId = '';
      this.selectedEventLabel = '';
      this.$emit('input', '');
    },
    formatMeta(event) {
      const time = event.fuzzyTime || (
        event.startTime ? this.$dayjs(event.startTime).format('YYYY-MM-DD') : ''
      );
      return [time, event.address].filter(Boolean).join(' · ');
    }
  }
};
</script>

<style scoped>
.event-link-autocomplete {
  width: 100%;
}
</style>

<style>
.event-link-autocomplete-popper {
  max-width: min(720px, 92vw);
}

.event-link-autocomplete-popper .el-autocomplete-suggestion li {
  line-height: 1.45;
  padding: 8px 14px;
}

.event-link-option-name {
  overflow: hidden;
  color: var(--text-primary);
  font-weight: 500;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.event-link-option-meta {
  overflow: hidden;
  margin-top: 3px;
  color: var(--text-secondary);
  font-size: 12px;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
